import { Fragment, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api, ApiError } from "@/lib/api";
import { useToast } from "@/features/toast/ToastProvider";
import { Panel } from "@/components/ui/Panel";
import { DataTable } from "@/components/ui/DataTable";
import { EmptyState } from "@/components/ui/EmptyState";
import { Pagination } from "@/components/ui/Pagination";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { formatDate } from "@bookboost/shared/format";

interface WebhookEvent {
  id: string;
  source: "stripe" | "hubspot";
  event_type: string;
  external_id: string | null;
  received_at: string | null;
  processed_at: string | null;
  status: "received" | "processed" | "failed" | "ignored";
  error: string | null;
}

const webhookSearch = z.object({
  status: z.enum(["received", "processed", "failed", "ignored"]).optional(),
  source: z.enum(["hubspot", "stripe"]).optional(),
  page: z.coerce.number().int().min(1).optional(),
});

export const Route = createFileRoute("/admin/webhooks")({
  validateSearch: webhookSearch,
  component: WebhooksPage,
});

function WebhooksPage() {
  const { status: statusFilter, source: sourceFilter, page } = Route.useSearch();
  const navigate = Route.useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const [pendingStatus, setPendingStatus] = useState(statusFilter ?? "");
  const [pendingSource, setPendingSource] = useState(sourceFilter ?? "");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [replaying, setReplaying] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "webhooks", { status: statusFilter, source: sourceFilter, page }],
    queryFn: () =>
      api.get<{ events: WebhookEvent[]; total: number; page: number; pageSize: number }>("/api/admin/webhooks", {
        searchParams: { status: statusFilter, source: sourceFilter, page },
      }),
  });

  const replay = useMutation({
    mutationFn: (id: string) =>
      api.post<{ ok: true; result: { status: "processed" | "failed"; error?: string } }>(
        `/api/admin/webhooks/${id}/replay`,
      ),
    onSuccess: (res) => {
      if (res.result.status === "failed") {
        toast.err(res.result.error ?? "Replay failed.");
      } else {
        toast.ok(`Replay ${res.result.status}.`);
      }
      // Prefix-matches the list + payload detail queries.
      qc.invalidateQueries({ queryKey: ["admin", "webhooks"] });
      // Failed-webhook count feeds the overview metric + tab badge.
      qc.invalidateQueries({ queryKey: ["admin", "overview"] });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't replay."),
    onSettled: () => setReplaying(null),
  });

  function onReplay(id: string) {
    setReplaying(id);
    replay.mutate(id);
  }

  const hasFilters = Boolean(statusFilter || sourceFilter);

  return (
    <>
      <h1>Webhook events</h1>

      <Panel>
        <form
          className="inline filters"
          onSubmit={(e) => {
            e.preventDefault();
            navigate({
              search: {
                status: (pendingStatus || undefined) as typeof statusFilter,
                source: (pendingSource || undefined) as typeof sourceFilter,
              },
            });
          }}
        >
          <div className="field-group">
            <label className="muted" htmlFor="source-filter">Source</label>
            <select
              id="source-filter"
              value={pendingSource}
              onChange={(e) => setPendingSource(e.target.value)}
            >
              <option value="">All</option>
              <option value="hubspot">hubspot</option>
              <option value="stripe">stripe</option>
            </select>
          </div>
          <div className="field-group">
            <label className="muted" htmlFor="status-filter">Status</label>
            <select
              id="status-filter"
              value={pendingStatus}
              onChange={(e) => setPendingStatus(e.target.value)}
            >
              <option value="">All</option>
              <option value="received">received</option>
              <option value="processed">processed</option>
              <option value="failed">failed</option>
              <option value="ignored">ignored</option>
            </select>
          </div>
          <button type="submit" className="primary">Filter</button>
          {hasFilters && (
            <Link to="/admin/webhooks" className="reset" onClick={() => { setPendingStatus(""); setPendingSource(""); }}>
              Reset
            </Link>
          )}
          <div className="spacer" />
          <Link
            to="/admin/webhooks"
            search={{ status: "failed" }}
            className={`quick-filter${statusFilter === "failed" ? " active" : ""}`}
          >
            <span className="quick-dot" /> Show failures only
          </Link>
        </form>
      </Panel>

      <Panel padding="tight">
        {isLoading ? (
          <p className="lede">Loading…</p>
        ) : error ? (
          <p className="err">{error instanceof Error ? error.message : "Failed to load events."}</p>
        ) : !data || data.events.length === 0 ? (
          <EmptyState
            icon="webhooks"
            title={hasFilters ? "No events match these filters" : "No webhook events yet"}
            body={
              hasFilters
                ? "Loosen filters to see all events."
                : "HubSpot and Stripe webhooks land here. If you expect events, check webhook endpoints and secrets in the provider dashboards."
            }
            actionHref={hasFilters ? "/admin/webhooks" : undefined}
            actionLabel={hasFilters ? "Reset filters" : undefined}
          />
        ) : (
          <DataTable>
            <table className="table-cards">
              <thead>
                <tr>
                  <th>Received</th>
                  <th>Source</th>
                  <th>Event</th>
                  <th>External ID</th>
                  <th>Status</th>
                  <th>Processed</th>
                  <th>Error</th>
                  <th aria-label="Payload" />
                </tr>
              </thead>
              <tbody>
                {data.events.map((e) => {
                  const isOpen = expanded === e.id;
                  return (
                    <Fragment key={e.id}>
                      <tr className={e.status === "failed" ? "failed-row" : undefined}>
                        <td data-label="Received" className="muted">{formatDate(e.received_at)}</td>
                        <td data-label="Source">{e.source}</td>
                        <td data-label="Event"><code>{e.event_type}</code></td>
                        <td data-label="External ID" className="muted"><code>{e.external_id ?? "—"}</code></td>
                        <td data-label="Status"><StatusBadge status={e.status} /></td>
                        <td data-label="Processed" className="muted">{formatDate(e.processed_at)}</td>
                        <td data-label="Error" className="err error-cell">{e.error ?? ""}</td>
                        <td data-label="" className="payload-toggle-cell">
                          {e.status === "failed" && (
                            <button
                              type="button"
                              className="replay-btn"
                              disabled={replaying === e.id}
                              onClick={() => onReplay(e.id)}
                            >
                              {replaying === e.id ? "Replaying…" : "Replay"}
                            </button>
                          )}
                          <button
                            type="button"
                            className="payload-toggle"
                            aria-expanded={isOpen}
                            onClick={() => setExpanded(isOpen ? null : e.id)}
                          >
                            {isOpen ? "Hide" : "Payload"}
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="payload-row">
                          <td colSpan={8}>
                            <WebhookPayload id={e.id} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </DataTable>
        )}
        {data && data.total > 0 && (
          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPage={(p) => navigate({ search: { status: statusFilter, source: sourceFilter, page: p } })}
          />
        )}
      </Panel>

      <style>{webhookPageStyles}</style>
    </>
  );
}

function WebhookPayload({ id }: { id: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "webhooks", "detail", id],
    queryFn: () => api.get<{ event: { payload: unknown; error: string | null } }>(`/api/admin/webhooks/${id}`),
    staleTime: 5 * 60 * 1000,
  });
  if (isLoading) return <p className="muted payload-loading">Loading payload…</p>;
  if (error || !data) return <p className="err payload-loading">Couldn't load payload.</p>;
  return (
    <div className="payload-detail">
      {data.event.error && <pre className="payload-error">{data.event.error}</pre>}
      <pre className="payload-json">{JSON.stringify(data.event.payload, null, 2)}</pre>
    </div>
  );
}

const webhookPageStyles = `
  .filters { flex-wrap: wrap; gap: 14px; align-items: flex-end; }
  .field-group { display: flex; flex-direction: column; gap: 4px; min-width: 140px; }
  .field-group label { font-size: 11.5px; }
  .reset { color: var(--muted); font-size: 12.5px; padding: 8px 10px; }
  .spacer { flex: 1; }
  .quick-filter {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 6px 12px; border-radius: 999px;
    background: var(--err-soft); color: var(--err);
    font-size: 12px; font-weight: 500;
    border: 1px solid color-mix(in srgb, var(--err) 22%, transparent);
    transition: background-color 150ms;
  }
  .quick-filter:hover { background: color-mix(in srgb, var(--err) 12%, white); text-decoration: none; }
  .quick-filter.active { background: var(--err); color: #fff; border-color: var(--err); }
  .quick-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
  .error-cell { max-width: 280px; font-size: 12px; word-break: break-word; }
  .failed-row td { background: color-mix(in srgb, var(--err-soft) 60%, var(--surface)); }
  .payload-toggle-cell { text-align: right; white-space: nowrap; }
  .replay-btn {
    font-size: 12px; padding: 4px 10px; border-radius: 6px; cursor: pointer;
    margin-right: 6px;
    border: 1px solid color-mix(in srgb, var(--err) 32%, transparent);
    background: var(--surface); color: var(--err); font-weight: 500;
  }
  .replay-btn:hover:not(:disabled) { background: var(--err-soft); border-color: var(--err); }
  .replay-btn:disabled { opacity: 0.6; cursor: default; }
  .payload-toggle {
    font-size: 12px; padding: 4px 10px; border-radius: 6px; cursor: pointer;
    border: 1px solid var(--border); background: var(--surface); color: var(--muted);
  }
  .payload-toggle:hover { color: var(--text-strong); border-color: var(--text-strong); }
  .payload-row td { padding: 0; }
  .payload-detail { padding: 12px 14px; }
  .payload-loading { padding: 12px 14px; margin: 0; }
  .payload-json, .payload-error {
    margin: 0; font-size: 11.5px; line-height: 1.5;
    white-space: pre-wrap; word-break: break-word;
    max-height: 420px; overflow: auto;
    background: color-mix(in srgb, var(--text-strong) 4%, transparent);
    border-radius: 8px; padding: 12px;
  }
  .payload-error { color: var(--err); margin-bottom: 10px; }
`;
